import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useStatesStore, useOrganizationStore } from '@/store';
import { TreeNode } from 'primevue/treenode';
import { RelayHubService } from '@/services';
import { AxiosResponse } from 'axios';
import { RelayHubInterface, StateInterface } from '@/types';
import { MEDICAID_FEATUREABLE_TYPE } from '@/enum';
import { useFlattenOrganizations } from '@/composables';

export const useRelayHubStore = defineStore('RelayHubStore', () => {
  const doToast = ref('doToast');
  const selectedMedicaidStates = ref<number[]>([]);
  const originalMedicaidStates = ref<number[]>([]);
  const enabledStates = ref<StateInterface[]>([]);
  const data = ref<RelayHubInterface>();
  const organizationsAbleToConnect = ref<TreeNode[]>([]);
  const statesAbleToConnect = ref<StateInterface[]>([]);
  const connectedOrganizations = ref<TreeNode[]>([]);
  const selectedOrganizationIds = ref<number[]>([]);
  const originalConnectedOrganizationIds = ref<number[]>([]);
  const loading = ref(false);
  const error = ref<boolean>(false);
  const message = ref<string>('');

  const catchError = (e: AxiosResponse) => {
    error.value = true;
    message.value = e.data.message;
    loading.value = false;
    return;
  };

  const fetchRelayHubConfig = async () => {
    load();

    const response = await RelayHubService.fetchRelayHubConfig().catch(catchError);

    if (response && response.status === 200) {
      data.value = response.data.data;
    }
    loading.value = false;
  };

  const load = () => {
    loading.value = true;
    error.value = false;
    message.value = '';
  };

  const fetchMedicaidEnabledStates = async () => {
    load();

    await useStatesStore().getStates(true).catch(catchError);

    const response = await RelayHubService.fetchMedicaidEnabledStates().catch(catchError);
    if (response) {
      originalMedicaidStates.value = response.data.data.model_ids_with_feature;
      selectedMedicaidStates.value = response.data.data.model_ids_with_feature;
      enabledStates.value = [];
      statesAbleToConnect.value = [];

      useStatesStore().states.forEach((state) => {
        if (originalMedicaidStates.value.find((id) => id === state.id)) enabledStates.value.push(state);
        else statesAbleToConnect.value.push(state);
      });
    }
    loading.value = false;
  };

  //disable medicaid for whatever model and ids are passed
  const disableMedicaid = async (idsToDisable: number[], model: MEDICAID_FEATUREABLE_TYPE) => {
    load();
    const promises = idsToDisable.map(async (id: number) => {
      return RelayHubService.disableMedicaid(id, model).catch(catchError);
    });
    await Promise.all(promises).catch(catchError);
  };

  //enable medicaid for whatever model and ids are passed
  const enableMedicaid = async (idsToEnable: number[], model: MEDICAID_FEATUREABLE_TYPE) => {
    load();
    const promises = idsToEnable.map(async (id: number) => {
      return RelayHubService.enableMedicaid(id, model);
    });

    await Promise.all(promises).catch(catchError);
  };

  //add new states from the addState modal
  const addNewStates = async (states: number[]) => {
    load();
    await enableMedicaid(states, MEDICAID_FEATUREABLE_TYPE.STATES);

    loading.value = false;
  };

  //enable/disable already added states
  const saveStateMedicareAssignments = async () => {
    load();

    const idsToDelete: number[] = [];

    originalMedicaidStates.value.forEach((id) => {
      const found = selectedMedicaidStates.value.find((selectedId) => selectedId === id);
      if (!found) idsToDelete.push(id);
    });

    await disableMedicaid(idsToDelete, MEDICAID_FEATUREABLE_TYPE.STATES);
    await enableMedicaid(selectedMedicaidStates.value, MEDICAID_FEATUREABLE_TYPE.STATES);

    loading.value = false;
  };

  //here we filter the organizationsAbleToConnect and connectedOrganizations by the selected states
  const filterOrganizations = async (flattenedOrganizations: TreeNode[]) => {
    connectedOrganizations.value = [];
    originalConnectedOrganizationIds.value = [];
    organizationsAbleToConnect.value = [];
    selectedOrganizationIds.value = [];

    //first get the states selected
    const filteredStates = useStatesStore().states.filter((state) =>
      originalMedicaidStates.value.find((id) => id === state.id),
    );

    //all orgs in those states
    const allStateOrgs = flattenedOrganizations.filter((org) =>
      filteredStates.find((state) => state.name === org.state),
    );

    const promises = allStateOrgs.map(async (org) => {
      return RelayHubService.isOrganizationEnabled(org.id);
    });

    await Promise.all(promises)
      .then((responses) => {
        responses.forEach((response, index: number) => {
          const org = allStateOrgs[index];

          if (response && response.data.data.length) {
            originalConnectedOrganizationIds.value.push(org.id);
            connectedOrganizations.value.push(org);
            selectedOrganizationIds.value.push(org.id);
          } else organizationsAbleToConnect.value.push(org);
        });
      })
      .catch(catchError);

    //resort alphabetically just to be sure Promises are returned in the same order
    connectedOrganizations.value.sort((a, b) => a.name.localeCompare(b.name));
    organizationsAbleToConnect.value.sort((a, b) => a.name.localeCompare(b.name));
  };

  // Fetch organizations and flatten the tree node
  const fetchOrganizations = async () => {
    load();

    const response = await useOrganizationStore().getOrganizations().catch(catchError);
    const flattenedOrganizations = useFlattenOrganizations(response as TreeNode[]);

    filterOrganizations(flattenedOrganizations);

    loading.value = false;
  };

  //connect a new organization
  const addNewOrganization = async (medicaid_district_code: string, id: number) => {
    load();

    await RelayHubService.connectOrganization(medicaid_district_code, id).catch(catchError);
    await enableMedicaid([id], MEDICAID_FEATUREABLE_TYPE.ORGANIZATIONS);

    loading.value = false;
  };

  const updateOrganizationDistrictCode = async (medicaid_district_code: string, id: number) => {
    await RelayHubService.connectOrganization(medicaid_district_code, id).catch(catchError);
  };

  //enable/disable already added organizations
  const saveOrganizationMedicareAssignments = async () => {
    load();

    const idsToDelete: number[] = [];

    originalConnectedOrganizationIds.value.forEach((id) => {
      const found = selectedOrganizationIds.value.find((selectedId) => selectedId === id);
      if (!found) idsToDelete.push(id);
    });

    await disableMedicaid(idsToDelete, MEDICAID_FEATUREABLE_TYPE.ORGANIZATIONS);
    await enableMedicaid(selectedOrganizationIds.value, MEDICAID_FEATUREABLE_TYPE.ORGANIZATIONS);

    loading.value = false;
  };

  return {
    data,
    fetchMedicaidEnabledStates,
    addNewOrganization,
    selectedMedicaidStates,
    connectedOrganizations,
    organizationsAbleToConnect,
    loading,
    error,
    fetchOrganizations,
    fetchRelayHubConfig,
    saveStateMedicareAssignments,
    saveOrganizationMedicareAssignments,
    enabledStates,
    disableMedicaid,
    enableMedicaid,
    statesAbleToConnect,
    addNewStates,
    selectedOrganizationIds,
    originalConnectedOrganizationIds,
    originalMedicaidStates,
    updateOrganizationDistrictCode,
    doToast,
    message,
  };
});

export default useRelayHubStore;
